import { FC, ReactElement, ReactNode, useCallback, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import {
  Progress,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { defaultDimensions, useOnResize } from 'src/hooks/useOnResize';

import classes from './List.module.scss';

export interface ListProps<T> {
  rows: T[];
  loadMore: (startIndex: number, stopIndex: number) => Promise<void>;
  itemSize: number;
  Item: FC<ListChildComponentProps<T[]>>;
  noResults: ReactNode;
  loading: ReactNode;
  isLoading: boolean;
}

interface NoResultsProps {
  children: ReactNode;
}

const NoResults: FC<NoResultsProps> = ({ children }) => {
  return (
    <div className={classes.noResults} data-testid='noResults'>
      <Typography>{children}</Typography>
    </div>
  );
};

interface LoadingProps {
  children: ReactNode;
}

const Loading: FC<LoadingProps> = ({ children }) => {
  return (
    <div className={classes.loading} data-testid='loading'>
      <Typography>{children}</Typography>
      <Progress type='solo' cycled data-testid='floorMapLoadingProgress' />
    </div>
  );
};

interface InnerProps<T> extends ListProps<T> {
  width: number;
  height: number;
}

const Inner = <T,>({
  rows,
  loadMore,
  width,
  height,
  itemSize,
  Item,
  noResults,
  isLoading,
  loading,
}: InnerProps<T>): ReactElement => {
  const isItemLoaded = useCallback((index: number) => !!rows[index], [rows]);

  if (rows.length) {
    return (
      <InfiniteLoader
        isItemLoaded={isItemLoaded}
        itemCount={Infinity}
        loadMoreItems={loadMore}
      >
        {({ onItemsRendered, ref }) => (
          <FixedSizeList
            itemCount={rows.length}
            onItemsRendered={onItemsRendered}
            ref={ref}
            height={height}
            width={width}
            itemSize={itemSize}
            itemData={rows}
            style={{ overflow: 'hidden auto' }}
          >
            {Item}
          </FixedSizeList>
        )}
      </InfiniteLoader>
    );
  }

  if (isLoading) return <Loading>{loading}</Loading>;

  return <NoResults>{noResults}</NoResults>;
};

export const List = <T,>(props: ListProps<T>): ReactElement => {
  const [dimensions, setDimensions] = useState(defaultDimensions);

  const { observe } = useOnResize(setDimensions);

  return (
    <div
      className={classes.container}
      ref={observe}
      data-testid='listContainer'
    >
      <Inner width={dimensions.width} height={dimensions.height} {...props} />
    </div>
  );
};
